if (!customElements.get('quick-add-bulk')) {
    customElements.define(
        'quick-add-bulk',
        class QuickAddBulk extends BulkAdd {
            constructor() {
                super();
                this.quantity = this.querySelector('quantity-input');

                const debouncedOnChange = debounce((event) => {
                    if (parseInt(event.target.value) === 0) {
                        this.startQueue(event.target.dataset.index, parseInt(event.target.value));
                    } else {
                        this.validateQuantity(event);
                    }
                }, ON_CHANGE_DEBOUNCE_TIMER);

                // Record the quantity the user is waiting for the moment the value changes.
                // Until the cart actually reflects it, ignore cart events from sibling steppers
                // (which carry the full cart where this variant isn't committed yet) and from
                // our own intermediate requests — this prevents the value flicker when adding
                // several variants, or tapping + quickly on the same one.
                this.addEventListener('change', (e) => {
                    var v = parseInt((e.target && e.target.value) || '0', 10);
                    this._desiredQty = isNaN(v) ? null : v;
                });
                this.addEventListener('change', debouncedOnChange.bind(this));
                this.listenForActiveInput();
                this.listenForKeydown();
                this.lastActiveInputId = null;
            }

            connectedCallback() {
                this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                    if (event.source === 'quick-add') {
                        // Still sync other instances of the same variant (e.g. sticky footer <-> in-flow)
                        if (event.cartData) {
                            this.updateFromCartData(event.cartData);
                        }
                        return;
                    }

                    const thisVariantId = parseInt(this.dataset.index);
                    const eventVariantId = event.variantId ? parseInt(event.variantId) : null;

                    if (eventVariantId && eventVariantId !== thisVariantId) {
                        return;
                    }

                    // If we have cart data in the event, update directly instead of re-fetching
                    if (event.cartData) {
                        this.updateFromCartData(event.cartData);
                        return;
                    }

                    this.onCartUpdate().then(() => {
                        this.listenForActiveInput();
                        this.listenForKeydown();
                    });
                });
            }

            disconnectedCallback() {
                if (this.cartUpdateUnsubscriber) {
                    this.cartUpdateUnsubscriber();
                }
            }

            // New method: update directly from cart data without re-fetching section
            updateFromCartData(cartData) {
                const variantId = this.dataset.index;
                const input = this.querySelector('.quantity__input');
                if (!input) return;

                let quantity = 0;
                const cartItem = cartData.items?.find(
                    item => String(item.variant_id) === variantId || String(item.id) === variantId
                );
                quantity = cartItem ? cartItem.quantity : 0;

                // While the user has a pending desired quantity, ignore any cart that does
                // not yet reflect it (sibling adds, or our own intermediate request). Once
                // the cart matches what they want, accept it and stop guarding.
                if (this._desiredQty != null) {
                    if (quantity !== this._desiredQty) return;
                    this._desiredQty = null;
                }

                // Update input value
                input.value = quantity;
                input.setAttribute('value', quantity);
                input.dataset.cartQuantity = String(quantity);

                // Update quantity-input class for styling
                const quantityInput = this.querySelector('quantity-input');
                if (quantityInput) {
                    if (quantity > 0) {
                        quantityInput.classList.add('quantity--has-items');
                    } else {
                        quantityInput.classList.remove('quantity--has-items');
                    }
                }

                // Update minus button state
                const minusBtn = this.querySelector('.quantity__button[name="minus"]');
                if (minusBtn) {
                    const min = parseInt(input.getAttribute('min') || '0', 10);
                    if (quantity <= min) {
                        minusBtn.classList.add('disabled');
                    } else {
                        minusBtn.classList.remove('disabled');
                    }
                }

                this.listenForActiveInput();
                this.listenForKeydown();
            }

            get input() {
                return this.querySelector('quantity-input input');
            }

            selectProgressBar() {
                return this.querySelector('.progress-bar-container');
            }

            listenForActiveInput() {
                if (!this.classList.contains('hidden')) {
                    this.input?.addEventListener('focusin', (event) => event.target.select());
                }
                this.isEnterPressed = false;
            }

            listenForKeydown() {
                this.input?.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter') {
                        this.input?.blur();
                        this.isEnterPressed = true;
                    }
                });
            }

            cleanErrorMessageOnType(event) {
                event.target.addEventListener(
                    'keypress',
                    () => {
                        event.target.setCustomValidity('');
                    },
                    { once: true }
                );
            }

            get sectionId() {
                if (!this._sectionId) {
                    this._sectionId =
                        this.dataset.sectionId ||
                        this.getAttribute('data-section-id') ||
                        this.closest('.collection-quick-add-bulk')?.dataset?.id ||
                        this.closest('[data-id]')?.dataset?.id;
                }
                return this._sectionId;
            }

            // Check if we're in a related-products or recommendations section
            isInRecommendationsSection() {
                return !!this.closest('product-recommendations');
            }

            onCartUpdate() {
                return new Promise((resolve, reject) => {
                    // For recommendations sections, fetch cart and update directly
                    if (this.isInRecommendationsSection()) {
                        fetch('/cart.js')
                            .then((response) => response.json())
                            .then((cart) => {
                                this.updateFromCartData(cart);
                                resolve();
                            })
                            .catch((e) => {
                                console.error(e);
                                reject(e);
                            });
                        return;
                    }

                    const url = new URL(this.getSectionsUrl(), window.location.origin);
                    url.searchParams.set('section_id', this.sectionId);

                    fetch(url.pathname + url.search)
                        .then((response) => response.text())
                        .then((responseText) => {
                            const html = new DOMParser().parseFromString(responseText, 'text/html');
                            const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
                            if (sourceQty) {
                                this.innerHTML = sourceQty.innerHTML;
                            } else {
                                // Fallback: fetch cart and update directly
                                return fetch('/cart.js')
                                    .then((response) => response.json())
                                    .then((cart) => {
                                        this.updateFromCartData(cart);
                                    });
                            }
                            resolve();
                        })
                        .catch((e) => {
                            console.error(e);
                            reject(e);
                        });
                });
            }

            getSectionsUrl() {
                const url = new URL(window.location.href);
                url.searchParams.delete('section_id');
                return url.pathname + url.search;
            }

            updateMultipleQty(items) {
                this.selectProgressBar().classList.remove('hidden');

                // Track if we're adding items (for drawer opening logic)
                const currentInput = this.querySelector('.quantity__input');
                const previousQty = currentInput ? parseInt(currentInput.dataset.cartQuantity || '0', 10) : 0;
                const newQty = Object.values(items)[0] || 0;
                const isAddingItems = newQty > previousQty;

                const ids = Object.keys(items);
                const body = JSON.stringify({
                    updates: items,
                    sections: this.getSectionsToRender().map((section) => section.section),
                    sections_url: this.getSectionsUrl(),
                });

                fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
                    .then((response) => {
                        return response.text();
                    })
                    .then((state) => {
                        const parsedState = JSON.parse(state);
                        // Failed cart update (e.g. 422 out of stock) returns no sections —
                        // don't render or publish a bogus cartUpdate, just surface it.
                        if (!parsedState || !parsedState.sections) {
                            console.error('Cart update failed:', parsedState && (parsedState.description || parsedState.message) || state);
                            this._desiredQty = null;
                            return;
                        }
                        this.renderSections(parsedState, ids);
                        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });

                        // Desktop only (>= 990px): open the cart drawer when adding items
                        // (not when decrementing). On tablet/mobile the add stays silent.
                        const shouldAutoOpen = window.innerWidth >= 990 && isAddingItems;

                        if (shouldAutoOpen) {
                            const cartDrawer = document.querySelector('cart-drawer');
                            if (cartDrawer && typeof cartDrawer.open === 'function') {
                                cartDrawer.open();
                            }
                        }
                    })
                    .catch((e) => {
                        console.error('Cart update error:', e);
                        this._desiredQty = null;
                    })
                    .finally(() => {
                        this.selectProgressBar().classList.add('hidden');
                        this.setRequestStarted(false);
                    });
            }

            getSectionsToRender() {
                // NOTE: we deliberately do NOT ask the server to re-render the host
                // section (the whole collection/product grid) — that render is what made
                // add-to-cart take several seconds. The stepper count is updated
                // optimistically from the returned cart JSON via the cartUpdate event
                // (updateFromCartData), so only the small cart UI needs server HTML.
                return [
                    {
                        id: 'cart-icon-bubble',
                        section: 'cart-icon-bubble',
                        selector: '#cart-icon-bubble',
                    },
                    {
                        id: 'CartDrawer',
                        section: 'cart-drawer',
                        selector: '#CartDrawer',
                    },
                ];
            }

            renderSections(parsedState, ids) {
                const intersection = this.queue.filter((element) => ids.includes(element.id));
                if (intersection.length !== 0) return;

                this.getSectionsToRender().forEach((section) => {
                    const sectionElement = document.getElementById(section.id);
                    if (!sectionElement) return;

                    const sectionHtml = parsedState.sections[section.section];
                    if (!sectionHtml) return;

                    // Handle cart drawer specially
                    if (section.section === 'cart-drawer') {
                        const cartDrawer = sectionElement.closest('cart-drawer') || document.querySelector('cart-drawer');
                        if (cartDrawer) {
                            const isEmpty = parsedState.items.length === 0;
                            cartDrawer.classList.toggle('is-empty', isEmpty);

                            const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
                            const newDrawerInner = html.querySelector('.drawer__inner');
                            const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
                            if (newDrawerInner && currentDrawerInner) {
                                currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
                            }
                        }
                        return;
                    }

                    // Handle cart icon bubble
                    if (section.section === 'cart-icon-bubble') {
                        const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
                        const newBubble = html.querySelector('#cart-icon-bubble') || html.querySelector('.shopify-section');
                        if (newBubble) {
                            sectionElement.innerHTML = newBubble.innerHTML;
                        }
                        return;
                    }

                    // Handle quick-add-bulk itself
                    const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
                    if (innerHtml) {
                        sectionElement.innerHTML = innerHtml;
                    } else {
                        // Fallback: update from cart data directly
                        this.updateFromCartData(parsedState);
                    }
                });

                if (this.isEnterPressed) {
                    this.querySelector(`#Quantity-${this.lastActiveInputId}`)?.select();
                }

                this.listenForActiveInput();
                this.listenForKeydown();
            }

            getSectionInnerHTML(html, selector = '.shopify-section') {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const element = doc.querySelector(selector);
                // Return innerHTML only if element found, otherwise return null (not the full HTML!)
                return element ? element.innerHTML : null;
            }
        }
    );
}